import React, { ReactNode, useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Button from './Button';
import './Layout.css';

interface LayoutProps {
  children: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [publicSidebarOpen, setPublicSidebarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('informes');
  const [isMobile, setIsMobile] = useState(false);
  const location = useLocation();
  const { isAuthenticated, user, logout } = useAuth();

  // Check if we're on an admin route
  const isAdminRoute = location.pathname.startsWith('/admin');

  // Handle responsive behavior
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setSidebarOpen(false); // Close sidebar on desktop
        setPublicSidebarOpen(false); // Close public sidebar on desktop
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close sidebar when route changes on mobile
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
      setPublicSidebarOpen(false);
    }

  // Set active section based on current route
    if (location.pathname === '/') {
      setActiveSection('informes');
    } else if (location.pathname === '/fotos') {
      setActiveSection('fotos');
    } else if (location.pathname.startsWith('/blog')) {
      setActiveSection('blog');
    } else if (location.pathname === '/videos') {
      setActiveSection('videos');
    } else if (location.pathname === '/dorothy') {
      setActiveSection('dorothy');
    }
  }, [location.pathname, isMobile]);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const togglePublicSidebar = () => {
    setPublicSidebarOpen(!publicSidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const closePublicSidebar = () => {
    setPublicSidebarOpen(false);
  };

  const handleLogout = () => {
    logout();
    closeSidebar();
  };

  // Navigation sections for public site
  const navigationSections = [
    { id: 'informes', label: 'Informes' },
    { id: 'fotos', label: 'Fotos' },
    { id: 'blog', label: 'Blog' },
    { id: 'videos', label: 'Videos' },
    { id: 'radar', label: 'Radar' },
    { id: 'satelite', label: 'Satélite' },
    { id: 'dorothy', label: 'Dorothy' },
    { id: 'nosotros', label: 'Nosotros' }
  ];

  // Social media links
  const socialLinks = [
    {
      name: 'Facebook',
      url: 'https://www.facebook.com/meteorologiaaficionadamardelplata/',
      color: '#1877F2',
      svgPath: <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    },
    {
      name: 'Instagram',
      url: 'https://www.instagram.com/mamdp.ok',
      color: '#E4405F',
      svgPath: <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
    },
    {
      name: 'WhatsApp',
      url: 'https://whatsapp.com/channel/0029VaJ0VGR7j6g8s2danM0O',
      color: '#25D366',
      svgPath: <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    },
    {
      name: 'YouTube',
      url: 'https://www.youtube.com/mamdpok',
      color: '#FF0000',
      svgPath: <path d="M23.495 6.205a3.007 3.007 0 00-2.088-2.088c-1.87-.501-9.396-.501-9.396-.501s-7.507-.01-9.396.501A3.007 3.007 0 00.527 6.205a31.247 31.247 0 00-.522 5.805 31.247 31.247 0 00.522 5.783 3.007 3.007 0 002.088 2.088c1.868.502 9.396.502 9.396.502s7.506 0 9.396-.502a3.007 3.007 0 002.088-2.088 31.247 31.247 0 00.5-5.783 31.247 31.247 0 00-.5-5.805zM9.609 15.601V8.408l6.264 3.602z"/>
    }
  ];

  const handleSectionChange = (sectionId: string) => {
    setActiveSection(sectionId);

    // Navigate to the appropriate route
    if (sectionId === 'informes') {
      window.location.href = '/';
    } else if (sectionId === 'fotos') {
      window.location.href = '/fotos';
    } else if (sectionId === 'blog') {
      window.location.href = '/blog';
    } else if (sectionId === 'videos') {
      window.location.href = '/videos';
    } else if (sectionId === 'dorothy') {
      window.location.href = '/dorothy';
    } else if (sectionId === 'radar') {
      window.open('https://www.smn.gob.ar/radar', '_blank');
    } else if (sectionId === 'satelite') {
      window.open('https://www.smn.gob.ar/satelite', '_blank');
    }
    // Add other navigation cases as needed

    // Close mobile sidebar after navigation
    if (isMobile) {
      closePublicSidebar();
    }
  };

  return (
    <div className={`layout ${isAdminRoute ? 'with-admin-sidebar' : 'with-public-sidebar'}`}>
      <header className="layout-header">
        <div className="header-logo-section">
          {isAdminRoute && (
            <button
              className="sidebar-toggle sidebar-toggle-over-logo"
              onClick={toggleSidebar}
              aria-label="Toggle navigation menu"
              aria-expanded={sidebarOpen}
            >
              <span className="hamburger-line"></span>
              <span className="hamburger-line"></span>
              <span className="hamburger-line"></span>
            </button>
          )}
          {!isAdminRoute && isMobile && (
            <button
              className="sidebar-toggle sidebar-toggle-over-logo"
              onClick={togglePublicSidebar}
              aria-label="Toggle navigation menu"
              aria-expanded={publicSidebarOpen}
            >
              <span className="hamburger-line"></span>
              <span className="hamburger-line"></span>
              <span className="hamburger-line"></span>
            </button>
          )}
        </div>
        <div className="header-image-section">
          <video
            className="header-video"
            autoPlay
            loop
            muted
            playsInline
          >
            <source src="/hmeteo3.mp4" type="video/mp4" />
          </video>
          {isAuthenticated && (
            <div className="header-right">
              <div className="user-info">
                <span className="user-name">{user?.name}</span>
                <span className="user-role">({user?.role})</span>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="logout-button"
              >
                Cerrar Sesión
              </Button>
            </div>
          )}
        </div>
      </header>

      {/* Public site navigation horizontal bar */}
      {!isAdminRoute && (
        <>
          <nav className="public-navbar">
            <div className="public-navbar-inner">
              <div className="public-navbar-sections">
                {navigationSections.map((section) => (
                  <button
                    key={section.id}
                    className={`nav-section-button ${activeSection === section.id ? 'active' : ''}`}
                    onClick={() => handleSectionChange(section.id)}
                  >
                    {section.label}
                  </button>
                ))}
                <a
                  href="https://www.lucasdastro.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="nav-section-button"
                  aria-label="Ver astrofotografía"
                >
                  Astrofotografía
                </a>
              </div>
              <div className="public-navbar-social">
                {socialLinks.map((social) => (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-navbar-link"
                    title={social.name}
                    style={{ '--social-color': social.color } as React.CSSProperties}
                  >
                    <svg className="social-svg" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                      {social.svgPath}
                    </svg>
                  </a>
                ))}
              </div>
            </div>
          </nav>

          {/* Mobile dropdown */}
          {publicSidebarOpen && (
            <div className="public-navbar-dropdown">
              <div className="public-navbar-dropdown-nav">
                {navigationSections.map((section) => (
                  <button
                    key={section.id}
                    className={`nav-section-button ${activeSection === section.id ? 'active' : ''}`}
                    onClick={() => handleSectionChange(section.id)}
                  >
                    {section.label}
                  </button>
                ))}
                <a
                  href="https://www.lucasdastro.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="nav-section-button"
                  onClick={closePublicSidebar}
                >
                  Astrofotografía
                </a>
              </div>
              <div className="social-links-divider"></div>
              <div className="social-links-title">Seguinos</div>
              <div className="public-navbar-dropdown-social">
                {socialLinks.map((social) => (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="social-link"
                    style={{ '--social-color': social.color } as React.CSSProperties}
                  >
                    <svg className="social-svg" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                      {social.svgPath}
                    </svg>
                    <span className="social-label">{social.name}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
          {publicSidebarOpen && isMobile && (
            <div className="sidebar-overlay" onClick={closePublicSidebar}></div>
          )}
        </>
      )}

      <div className={`layout-body ${!isAdminRoute ? 'with-public-navbar' : ''}`}>
        {isAdminRoute && (
          <>
            <nav className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
              <div className="sidebar-content">
                <div className="sidebar-header">
                  <h2 className="sidebar-title">Panel Administrativo</h2>
                  {user && (
                    <div className="sidebar-user-info">
                      <p className="sidebar-user-name">{user.name}</p>
                      <p className="sidebar-user-role">{user.role}</p>
                    </div>
                  )}
                </div>
                <ul className="sidebar-nav">
                  <li>
                    <Link to="/admin" className="sidebar-link" onClick={closeSidebar}>
                      Dashboard
                    </Link>
                  </li>
                  <li>
                    <Link to="/admin/reports" className="sidebar-link" onClick={closeSidebar}>
                      Informes
                    </Link>
                  </li>
                  <li>
                    <Link to="/admin/photos" className="sidebar-link" onClick={closeSidebar}>
                      Fotos
                    </Link>
                  </li>
                  <li>
                    <Link to="/admin/blog" className="sidebar-link" onClick={closeSidebar}>
                      Blog
                    </Link>
                  </li>
                  <li>
                    <Link to="/admin/videos" className="sidebar-link" onClick={closeSidebar}>
                      Videos
                    </Link>
                  </li>
                  <li>
                    <Link to="/admin/users" className="sidebar-link" onClick={closeSidebar}>
                      Usuarios
                    </Link>
                  </li>
                  <li>
                    <Link to="/admin/telemetry" className="sidebar-link" onClick={closeSidebar}>
                      Telemetría y Radar
                    </Link>
                  </li>
                  <li>
                    <Link to="/admin/dorothy/alerts" className="sidebar-link" onClick={closeSidebar}>
                      Alertas Dorothy
                    </Link>
                  </li>
                  <li>
                    <Link to="/" className="sidebar-link" onClick={closeSidebar}>
                      ← Sitio Público
                    </Link>
                  </li>
                </ul>
                
                <div className="sidebar-footer">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleLogout}
                    className="sidebar-logout-button"
                  >
                    Cerrar Sesión
                  </Button>
                </div>
              </div>
            </nav>
            {sidebarOpen && <div className="sidebar-overlay" onClick={closeSidebar}></div>}
          </>
        )}
        
        <main className={`layout-main ${isAdminRoute ? 'with-sidebar' : ''}`}>
          {isAdminRoute ? children : (
            <div className="public-content">
              {activeSection === 'informes' && children}
              {activeSection === 'fotos' && children}
              {activeSection === 'blog' && children}
              {activeSection === 'videos' && children}
              {activeSection === 'dorothy' && children}
              {activeSection === 'radar' && (
                <div className="section-content">
                  <div className="section-header">
                    <h1 className="section-title">🌧️ Radar Meteorológico</h1>
                    <p className="section-subtitle">
                      Imágenes de radar para el seguimiento de precipitaciones y sistemas meteorológicos.
                    </p>
                  </div>
                  <div className="content-placeholder">
                    <div className="placeholder-icon">🌧️</div>
                    <p className="placeholder-text">Radar meteorológico próximamente disponible</p>
                  </div>
                </div>
              )}
              {activeSection === 'nosotros' && (
                <div className="section-content">
                  <div className="section-header">
                    <h1 className="section-title">👥 Sobre Nosotros</h1>
                    <p className="section-subtitle">
                      Conoce más sobre nuestro equipo de meteorología aficionada.
                    </p>
                  </div>
                  <div className="about-content">
                    <div className="profile-card">
                      <div className="profile-image-container">
                        <img src="/Lucas.jpeg" alt="Lucas D'Ortone" className="profile-image" />
                      </div>
                      <div className="profile-info">
                        <h3>Lucas D'Ortone</h3>
                        <p className="profile-title">Astrofotógrafo</p>
                        <p className="profile-bio">
                          Soy astrofotógrafo con más de 15 años de experiencia, dedicado a la planificación, captura y procesado de imágenes del cielo nocturno en cielos oscuros. Mi trabajo se apoya en la precisión técnica y en el registro fiel de los fenómenos astronómicos.
                        </p>
                        <p className="profile-bio">
                          En mis comienzos con la astrofotografía, una noche durante una tormenta despertó también mi interés por la meteorología. A partir de esa experiencia, hace 14 años fundamos junto a amigos una página dedicada al seguimiento y análisis de fenómenos meteorológicos, combinando observación y datos.
                        </p>
                        <p className="profile-bio">
                          He realizado trabajos para campañas publicitarias internacionales, entre ellas proyectos para Corona, donde la planificación astronómica y las condiciones naturales fueron parte central del proceso creativo.
                        </p>
                        <p className="profile-bio">
                          También desarrollé proyectos fotográficos en áreas naturales protegidas, como La Payunia, realizando las primeras imágenes astronómicas autorizadas dentro del área, integrando paisaje volcánico, cielo nocturno y documentación técnica.
                        </p>
                        <p className="profile-bio">
                          Además, dicto talleres presenciales y virtuales de astrofotografía, abordando todo el proceso: planificación, trabajo en campo y procesado, y realizo proyectos por encargo con un enfoque técnico y profesional.
                        </p>
                      </div>
                    </div>
                    <div className="profile-card">
                      <div className="profile-image-container">
                        <img src="/Fede.jpeg" alt="Fede García" className="profile-image" />
                      </div>
                      <div className="profile-info">
                        <h3>Fede García</h3>
                        <p className="profile-title">Fan Cofounder</p>
                        <p className="profile-bio">
                          Estábamos en mi casa, en Sierras de los Padres, cuando todo esto empezó. Una hermosa tormenta nos inyectó las ganas de conocer más y seguir los fenómenos del clima marplatense.
                        </p>
                        <p className="profile-bio">
                          Desde entonces aporto mis perspectivas puramente experienciales al grupo, como un seguidor más, pero resulta que tuve la suerte de estar en el momento y el lugar indicado, asique podríamos decir que soy el Presidente del Fan Club de MAMDP.
                        </p>
                        <p className="profile-bio">
                          El club no existe, pero alguien tenía que ocupar el cargo.
                        </p>
                      </div>
                    </div>
                    <div className="profile-card">
                      <div className="profile-image-container">
                        <img src="/CristianO.jpeg" alt="Cristian Ovando" className="profile-image" />
                      </div>
                      <div className="profile-info">
                        <h3>Cristian Ovando</h3>
                        <p className="profile-title">Sobreviviente de tornado</p>
                        <p className="profile-bio">
                          Como no sabía como definirme, me pregunté: ¿Cómo empecé en todo esto? La respuesta tiene nombre y fecha: <a href="https://es.wikipedia.org/wiki/Oleada_de_tornados_de_Buenos_Aires_de_1993" target="_blank" rel="noopener noreferrer">La Oleada de Tornados de la Provincia de Buenos Aires el 13 de Abril de 1993</a>. El barrio donde aún vivo fue gravemente afectado por un tornado categoría F2 que barrió Mar del Plata desde el extremo norte hasta el centro. Mi casa solo sufrió daños menores, pero hubo que reconstruír casas de varios vecinos los meses siguientes. El evento me afectó profundamente: primero estuve traumado varios años por las tormentas, y luego gradualmente se fue convirtiendo en una pasión. A partir de mis 20 y en mis 30 me he encontrado varias veces en autos debajo de tormentas, gastando nafta para correr una tormenta que va a pasar por Miramar o La Brava, o protegido con botas e impermeable solo para dar una vuelta por Varese en una sudestada, o en el medio de la nada a las 00hs con -1ºC para sacar una foto del centro galáctico.
                        </p>
                        <p className="profile-bio">
                          No se como definirme, pero principalmente soy un apasionado del cielo, del espacio exterior y de Mar del Plata. Soy afortunado de tener un grupo de amigos con el que compartir esto.
                        </p>
                      </div>
                    </div>
                    <div className="profile-card">
                      <div className="profile-image-container">
                        <img src="/MatiasM.jpeg" alt="Matias Machado" className="profile-image" />
                      </div>
                      <div className="profile-info">
                        <h3>Matias Machado</h3>
                        <p className="profile-title">Un aficionado más</p>
                        <p className="profile-bio">
                          Soy un apasionado del tiempo y de todo lo que pasa en el cielo de Mar del Plata.
                        </p>
                        <p className="profile-bio">
                          Hace años observo, aprendo y trato de entender por qué acá el clima hace lo que quiere y casi nunca lo que esperamos.
                        </p>
                        <p className="profile-bio">
                          Llegué a este grupo casi de casualidad, cuando me apareció una foto de Lucas en Facebook y me fascinó lo que hacía. Desde ahí empecé a meterme cada vez más.
                        </p>
                        <p className="profile-bio">
                          Me gusta explicar lo que se ve y lo que puede pasar de manera simple, sin exagerar y con un poco de humor marplatense, porque en esta ciudad mirar el cielo es casi una costumbre.
                        </p>
                        <p className="profile-bio">
                          No soy meteorólogo profesional, solo alguien curioso que disfruta interpretar la atmósfera y compartirlo.
                        </p>
                      </div>
                    </div>
                    <div className="profile-card">
                      <div className="profile-image-container">
                        <img src="/MatiasP.png" alt="Matias Pees" className="profile-image" />
                      </div>
                      <div className="profile-info">
                        <h3>Matias Pees</h3>
                        <p className="profile-title">Informático</p>
                        <p className="profile-bio">
                          Soy informático con casi 20 años de experiencia, dedicado a la arquitectura de soluciones cloud en AWS y Azure, así como tecnología on-premise.
                        </p>
                        <p className="profile-bio">
                          Desde chico siempre sentí una atracción fuerte por las tormentas eléctricas y los fenómenos meteorológicos de mayor impacto. Luego de haber sobrevivido a la caída de una centella a pocos metros de distancia en Córdoba, las tormentas se convirtieron en fascinación para mi.
                        </p>
                        <p className="profile-bio">
                          En el año 2018, durante una cacería por la ruta 88, compartí unas imágenes al facebook de MAMDP y desde ese momento me adoptaron como uno más en este gran grupo de amigos.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </main>
      </div>
      
      <footer className="layout-footer">
        <p>&copy; 2026 Meteorología Aficionada Mar del Plata (MAMDP). No somos meteorólogos profesionales, solo aficionados. Acá solo encontrarán opiniones y nunca pronósticos.</p>
      </footer>
    </div>
  );
};

export default Layout;